import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { generatePath, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, ArrowLeft, Eye, EyeOff, FileText, LayoutList, Send } from "lucide-react";

import { cn } from "@/libs/cn";
import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import Button from "@/modules/common/components/files/Button";
import DeleteModal from "@/modules/common/components/files/DeleteModal";
import Input from "@/modules/common/components/files/Input";
import { Modal, ModalBody } from "@/modules/common/components/files/Modal";
import PageBreadcrumb from "@/modules/common/components/files/PageBreadcrumb";
import SelectV2 from "@/modules/common/components/files/SelectV2";
import Spinner from "@/modules/common/components/files/Spinner";
import Tag from "@/modules/common/components/files/Tag";
import useNavigateBack from "@/modules/common/hooks/useNavigateBack";
import useToastOnError from "@/modules/common/hooks/useToastOnError";
import { AdminUserRole } from "@/modules/common/types/Users.enum";
import { getApiErrorMessage } from "@/modules/common/utils/apiError";
import { ROUTES } from "@/routes/constants/routes";
import { ADMIN_KNOWLEDGE_BASE_ENDPOINTS, AdminKnowledgeBaseService } from "@/service/api/admin-knowledge-base";
import { KnowledgeBaseContentResponse } from "@/service/response/admin-knowledge-base";

import BlockEditor from "../components/BlockEditor";
import MarkdownPreview from "../components/MarkdownPreview";
import {
  KNOWLEDGE_BASE_AUDIENCE_OPTIONS,
  KNOWLEDGE_BASE_CATEGORY_OPTIONS,
  KNOWLEDGE_BASE_TYPE_OPTIONS,
  KnowledgeBaseStatus,
  knowledgeBaseStatusLabel,
} from "../constants";
import { blocksToMarkdown, ContentBlock, markdownToBlocks, previewUrlsByKey, withPreviewUrls } from "../markdown";
import { hasPendingUploads } from "../media/media-placeholder";

const DocumentEditor = lazy(() => import("../components/DocumentEditor"));

type EditorMode = "document" | "blocks";

const statusVariant = (status: number) => {
  if (status === KnowledgeBaseStatus.PUBLISHED) return "success" as const;
  if (status === KnowledgeBaseStatus.ARCHIVED) return "neutral" as const;
  return "warning" as const;
};

const KnowledgeBaseEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { canUserSee } = useSessionStore();
  const canWrite = canUserSee(AdminUserRole.ADMIN);
  const navigateBack = useNavigateBack(ROUTES.KNOWLEDGE_BASE.LIST);

  const detail = useQuery({
    queryKey: [ADMIN_KNOWLEDGE_BASE_ENDPOINTS.LIST, id],
    queryFn: () => AdminKnowledgeBaseService.get(id!),
    enabled: Boolean(id),
  });

  useToastOnError(detail.isError, detail.error, "Não foi possível carregar este conteúdo.", navigateBack);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<string>();
  const [type, setType] = useState<string>();
  const [audiences, setAudiences] = useState<string[]>([]);
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);
  const [documentMarkdown, setDocumentMarkdown] = useState("");
  const [editorMode, setEditorMode] = useState<EditorMode>(
    (location.state as { editorMode?: EditorMode } | null)?.editorMode ?? "document",
  );
  const [, setPreviewRevision] = useState(0);
  const mediaRefreshed = useRef(false);
  const [previewByKey, setPreviewByKey] = useState<Map<string, string>>(() => new Map());
  const [status, setStatus] = useState(KnowledgeBaseStatus.DRAFT);
  const [hydratedId, setHydratedId] = useState<string>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pending, setPending] = useState<string>();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const touch =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setDirty(true);
    };

  if (detail.data && hydratedId !== detail.data.id) {
    setTitle(detail.data.title);
    setCategory(String(detail.data.category));
    setType(String(detail.data.type));
    setAudiences(detail.data.audiences.map(String));
    setStatus(detail.data.status);
    const previews = previewUrlsByKey(detail.data.content, detail.data.renderedContent ?? "");
    setPreviewByKey(previews);
    setDocumentMarkdown(detail.data.content);
    setBlocks(markdownToBlocks(detail.data.content, previews));
    setHydratedId(detail.data.id);
  }

  const locked = !canWrite || status === KnowledgeBaseStatus.ARCHIVED;
  const currentMarkdown = () => (editorMode === "blocks" ? blocksToMarkdown(blocks) : documentMarkdown);
  const previewMarkdown =
    editorMode === "blocks"
      ? blocksToMarkdown(
          blocks.map((block) =>
            (block.kind === "image" || block.kind === "video") && block.previewUrl ? { ...block, key: block.previewUrl } : block,
          ),
        )
      : withPreviewUrls(documentMarkdown, previewByKey);

  const remember = (content: KnowledgeBaseContentResponse) => {
    setStatus(content.status);
    setHydratedId(content.id);
    setDirty(false);
    queryClient.setQueryData([ADMIN_KNOWLEDGE_BASE_ENDPOINTS.LIST, content.id], content);
    queryClient.invalidateQueries({ queryKey: [ADMIN_KNOWLEDGE_BASE_ENDPOINTS.LIST] });
  };

  const buildPayload = () => {
    if (!title.trim() || !category || !type || !audiences.length) {
      toast.error("Preencha título, categoria, tipo e público.");
      return null;
    }

    if (hasPendingUploads(currentMarkdown())) {
      toast.error("Aguarde o envio das mídias terminar.");
      return null;
    }

    return {
      title: title.trim(),
      category: Number(category),
      type: Number(type),
      audiences: audiences.map(Number),
      content: currentMarkdown(),
    };
  };

  const openCreated = (content: KnowledgeBaseContentResponse) => {
    if (id) return;
    navigate(generatePath(ROUTES.KNOWLEDGE_BASE.EDIT, { id: content.id }), { replace: true, state: { editorMode } });
  };

  const save = async (status?: KnowledgeBaseStatus) => {
    const payload = buildPayload();
    if (!payload) return null;

    if (!id) {
      const created = await AdminKnowledgeBaseService.create(payload);
      remember(created);
      if (!status || status === created.status) return created;

      try {
        const updated = await AdminKnowledgeBaseService.update(created.id, { status });
        remember(updated);
        return updated;
      } finally {
        openCreated(created);
      }
    }

    const updated = await AdminKnowledgeBaseService.update(id, { ...payload, status });
    remember(updated);
    return updated;
  };

  const refreshPreviewUrls = async () => {
    if (!id || mediaRefreshed.current) return;
    mediaRefreshed.current = true;

    const { data } = await detail.refetch();
    if (!data) return;

    const fresh = previewUrlsByKey(data.content, data.renderedContent ?? "");
    fresh.forEach((url, key) => previewByKey.set(key, url));
    setBlocks((current) =>
      current.map((block) => (block.key && fresh.has(block.key) ? { ...block, previewUrl: fresh.get(block.key)! } : block)),
    );
    setPreviewRevision((revision) => revision + 1);
  };

  const openPreview = () => {
    mediaRefreshed.current = false;
    setPreviewOpen(true);
  };

  const run = async (action: string, task: () => Promise<void>) => {
    setPending(action);
    try {
      await task();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível concluir a ação."));
    } finally {
      setPending(undefined);
    }
  };

  const saveDraft = () =>
    run("draft", async () => {
      const saved = await save();
      if (!saved) return;
      toast.success(saved.status === KnowledgeBaseStatus.PUBLISHED ? "Conteúdo salvo." : "Rascunho salvo.");
      openCreated(saved);
    });

  const publish = () =>
    run("publish", async () => {
      if (!currentMarkdown().trim()) {
        toast.error("Escreva o conteúdo antes de publicar.");
        return;
      }
      const saved = await save(KnowledgeBaseStatus.PUBLISHED);
      if (!saved) return;
      toast.success("Conteúdo publicado.");
      openCreated(saved);
    });

  const changeStatus = (action: "unpublish" | "archive" | "unarchive") =>
    run(action, async () => {
      if (!id) return;
      if (action === "unarchive") {
        remember(await AdminKnowledgeBaseService.update(id, { status: KnowledgeBaseStatus.DRAFT }));
      } else {
        const saved = await save(action === "archive" ? KnowledgeBaseStatus.ARCHIVED : KnowledgeBaseStatus.DRAFT);
        if (!saved) return;
      }
      toast.success("Status atualizado.");
    });

  if (id && detail.isLoading) {
    return (
      <div className="flex min-h-80 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (id && detail.isError) return null;

  const switchMode = (mode: EditorMode) => {
    if (mode === editorMode) return;
    if (hasPendingUploads(currentMarkdown())) {
      toast.error("Aguarde o envio das mídias terminar antes de trocar o modo de edição.");
      return;
    }
    if (mode === "document") {
      blocks.forEach((block) => {
        if (block.key && block.previewUrl) previewByKey.set(block.key, block.previewUrl);
      });
      setDocumentMarkdown(blocksToMarkdown(blocks));
    } else {
      setBlocks(markdownToBlocks(documentMarkdown, previewByKey));
    }
    setEditorMode(mode);
  };

  return (
    <div className="flex flex-col gap-5">
      <PageBreadcrumb
        items={[
          { label: "Central de Ajuda", to: ROUTES.HELP.HOME },
          { label: "Base de Conhecimento", to: ROUTES.KNOWLEDGE_BASE.LIST },
          { label: id ? "Editar conteúdo" : "Novo conteúdo" },
        ]}
      />

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-[22px] font-bold text-ui-primary">{id ? "Editar conteúdo" : "Novo conteúdo"}</h1>
          <Tag label={knowledgeBaseStatusLabel(status)} variant={statusVariant(status)} size="small" />
        </div>
        <Button type="button" variant="outlined" color="primary" icon={<ArrowLeft size={16} />} label="Voltar" onClick={navigateBack} />
      </header>

      <section className="rounded-3xl border border-ui-border bg-white p-5 shadow-sm md:p-6">
        <h2 className="text-base font-semibold text-ui-primary">Informações</h2>
        <p className="mt-1 text-sm text-ui-text-muted">Quem vê este conteúdo e em qual parte da central ele aparece.</p>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Input
              label="Título"
              markAsRequired
              disabled={locked}
              value={title}
              placeholder="Ex.: Como consultar o Relatório de GD"
              onChange={(event) => touch(setTitle)(event.target.value)}
            />
          </div>
          <SelectV2
            mode="single"
            label="Categoria"
            markAsRequired
            disabled={locked}
            options={KNOWLEDGE_BASE_CATEGORY_OPTIONS}
            placeholder="Selecione"
            value={category}
            onChange={touch(setCategory)}
          />
          <SelectV2
            mode="single"
            label="Tipo de conteúdo"
            markAsRequired
            disabled={locked}
            options={KNOWLEDGE_BASE_TYPE_OPTIONS}
            placeholder="Selecione"
            value={type}
            onChange={touch(setType)}
          />
          <div className="md:col-span-2">
            <SelectV2
              label="Disponível para"
              markAsRequired
              disabled={locked}
              options={KNOWLEDGE_BASE_AUDIENCE_OPTIONS}
              placeholder="Gestora, cliente ou ambos"
              value={audiences}
              onChange={touch(setAudiences)}
            />
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-ui-border bg-white p-5 shadow-sm md:p-6">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-ui-primary">Conteúdo</h2>
            <p className="mt-1 text-sm text-ui-text-muted">
              {editorMode === "document"
                ? "Escreva como num editor de texto. Para subtítulo, comece uma linha nova com ## e espaço (ou escolha Título 2 no menu Parágrafo). Para lista, comece com - e espaço."
                : "Monte o artigo em blocos e reordene como preferir."}
            </p>
          </div>
          <div role="tablist" aria-label="Modo de edição" className="inline-flex shrink-0 self-start rounded-xl bg-[#EEF1F5] p-1">
            {EDITOR_MODES.map(({ mode, label, icon: Icon }) => (
              <button
                key={mode}
                type="button"
                role="tab"
                aria-selected={editorMode === mode}
                onClick={() => switchMode(mode)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition",
                  editorMode === mode ? "bg-white font-medium text-ui-primary shadow-sm" : "text-ui-text-muted hover:text-ui-primary",
                )}
              >
                <Icon size={15} />
                {label}
              </button>
            ))}
          </div>
        </div>
        {editorMode === "document" ? (
          <Suspense
            fallback={
              <div className="flex min-h-80 items-center justify-center">
                <Spinner />
              </div>
            }
          >
            <DocumentEditor
              key={hydratedId ?? "new"}
              markdown={documentMarkdown}
              disabled={locked}
              previewByKey={previewByKey}
              onChange={(markdown, initialNormalize) => {
                setDocumentMarkdown(markdown);
                if (!initialNormalize) setDirty(true);
              }}
            />
          </Suspense>
        ) : (
          <BlockEditor blocks={blocks} disabled={locked} onChange={touch(setBlocks)} />
        )}
      </section>

      <footer className="sticky -bottom-6 z-20 -mx-6 -mb-6 flex flex-wrap items-center justify-between gap-3 border-t border-ui-border bg-white/95 px-6 py-3 backdrop-blur">
        <div className="flex flex-wrap items-center gap-2">
          <SaveState dirty={dirty} hasId={Boolean(id)} locked={locked} status={status} />
          {canWrite && status === KnowledgeBaseStatus.PUBLISHED && (
            <Button
              type="button"
              variant="text"
              color="warning"
              size="small"
              icon={<EyeOff size={15} />}
              label="Despublicar"
              loading={pending === "unpublish"}
              disabled={Boolean(pending)}
              onClick={() => changeStatus("unpublish")}
            />
          )}
          {canWrite && status !== KnowledgeBaseStatus.ARCHIVED && id && (
            <Button
              type="button"
              variant="text"
              color="danger"
              size="small"
              icon={<Archive size={15} />}
              label="Arquivar"
              disabled={Boolean(pending)}
              onClick={() => setArchiveOpen(true)}
            />
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button type="button" variant="outlined" color="primary" icon={<Eye size={16} />} label="Pré-visualizar" onClick={openPreview} />
          {canWrite && status === KnowledgeBaseStatus.ARCHIVED && (
            <Button
              type="button"
              color="primary"
              icon={<ArchiveRestore size={16} />}
              label="Restaurar"
              loading={pending === "unarchive"}
              disabled={Boolean(pending)}
              onClick={() => changeStatus("unarchive")}
            />
          )}
          {canWrite && status === KnowledgeBaseStatus.PUBLISHED && (
            <Button
              type="button"
              color="success"
              label="Salvar alterações"
              loading={pending === "draft"}
              disabled={Boolean(pending)}
              onClick={saveDraft}
            />
          )}
          {canWrite && status === KnowledgeBaseStatus.DRAFT && (
            <>
              <Button
                type="button"
                variant="outlined"
                color="primary"
                label="Salvar rascunho"
                loading={pending === "draft"}
                disabled={Boolean(pending)}
                onClick={saveDraft}
              />
              <Button
                type="button"
                icon={<Send size={15} />}
                label="Publicar"
                loading={pending === "publish"}
                disabled={Boolean(pending)}
                onClick={publish}
              />
            </>
          )}
        </div>
      </footer>

      <Modal open={previewOpen} onOpenChange={setPreviewOpen} title={title.trim() || "Pré-visualização"} className="max-w-3xl">
        <ModalBody className="mt-4 max-h-[65vh] overflow-y-auto">
          <MarkdownPreview markdown={previewMarkdown} onMediaError={() => void refreshPreviewUrls()} />
        </ModalBody>
      </Modal>

      <DeleteModal
        open={archiveOpen}
        title="Arquivar conteúdo"
        description="O conteúdo sai da central de ajuda e fica somente leitura até ser restaurado."
        confirmText="Arquivar"
        loading={pending === "archive"}
        onClose={() => setArchiveOpen(false)}
        onDelete={() => changeStatus("archive").then(() => setArchiveOpen(false))}
      />
    </div>
  );
};

const EDITOR_MODES = [
  { mode: "document" as const, label: "Documento", icon: FileText },
  { mode: "blocks" as const, label: "Blocos", icon: LayoutList },
];

const SaveState = ({ dirty, hasId, locked, status }: { dirty: boolean; hasId: boolean; locked: boolean; status: number }) => {
  if (locked) {
    return (
      <span className="mr-2 text-sm text-ui-text-muted">
        {status === KnowledgeBaseStatus.ARCHIVED ? "Arquivado — somente leitura" : "Somente leitura"}
      </span>
    );
  }

  const [dot, text] =
    dirty || !hasId ? ["bg-[#E5A000]", hasId ? "Alterações não salvas" : "Ainda não salvo"] : ["bg-[#99C32C]", "Tudo salvo"];
  return (
    <span className="mr-2 inline-flex items-center gap-2 text-sm text-ui-text-muted">
      <span className={cn("size-2 rounded-full", dot)} />
      {text}
    </span>
  );
};

export default KnowledgeBaseEditor;
